import React, { useState } from "react";
import {
  Paper,
  TextInput,
  Button,
  Group,
  Title,
  Container,
  List,
  Flex
} from "@mantine/core";
import { setDoc, doc } from "firebase/firestore";
import { db } from "../utils/firebase";
import { notifications } from "@mantine/notifications";

const Edit = ({ selectedDocument, setSelectedDocument, refreshSources }) => {
  const [saving, setSaving] = useState(false);

  if (!selectedDocument) return null;

  // Handle simple field updates
  const handleUpdateField = (field, value) => {
    setSelectedDocument((prevDoc) => ({
      ...prevDoc,
      fields: {
        ...prevDoc.fields,
        [field]: value
      }
    }));
  };

  // Handle updating an object inside an array
  const handleUpdateObjectField = (field, index, key, value) => {
    const updatedArray = [...selectedDocument.fields[field]];
    updatedArray[index] = { ...updatedArray[index], [key]: value };

    setSelectedDocument((prevDoc) => ({
      ...prevDoc,
      fields: {
        ...prevDoc.fields,
        [field]: updatedArray
      }
    }));
  };

  // Handle adding a new item (string or object)
  const handleAddArrayItem = (field) => {
    const fieldArray = selectedDocument.fields[field] || [];

    if (fieldArray.length > 0 && typeof fieldArray[0] === "object") {
      const newObject = Object.fromEntries(
        Object.keys(fieldArray[0]).map((key) => [key, ""])
      );
      setSelectedDocument((prevDoc) => ({
        ...prevDoc,
        fields: {
          ...prevDoc.fields,
          [field]: [...prevDoc.fields[field], newObject]
        }
      }));
    } else {
      setSelectedDocument((prevDoc) => ({
        ...prevDoc,
        fields: {
          ...prevDoc.fields,
          [field]: [...prevDoc.fields[field], ""]
        }
      }));
    }
  };

  // Handle removing an item from an array
  const handleRemoveArrayItem = (field, index) => {
    setSelectedDocument((prevDoc) => ({
      ...prevDoc,
      fields: {
        ...prevDoc.fields,
        [field]: prevDoc.fields[field].filter((_, i) => i !== index)
      }
    }));
  };

  // Function to save data to Firestore
  const handleSave = async () => {
    if (
      !selectedDocument ||
      !selectedDocument.collection ||
      !selectedDocument.source
    ) {
      notifications.show({
        title: "Error",
        message: "Invalid document reference.",
        color: "red",
        autoClose: 2000,
        withCloseButton: false
      });
      return;
    }

    setSaving(true);

    try {
      const docRef = doc(
        db,
        "sources",
        selectedDocument.source,
        selectedDocument.collection,
        "settings"
      );

      await setDoc(docRef, { ...selectedDocument.fields });

      notifications.show({
        title: "Success",
        message: "Document saved successfully!",
        color: "green",
        autoClose: 2000,
        withCloseButton: false
      });

      refreshSources(); // ✅ Reload sources & entities dynamically
    } catch (error) {
      notifications.show({
        title: "Error",
        message: `Failed to save: ${error.message}`,
        color: "red",
        autoClose: 2000,
        withCloseButton: false
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Paper
        shadow="md"
        p="lg"
        mt="md"
        radius="md"
        style={{
          backgroundColor: "#f8f4ff",
          maxWidth: "600px",
          margin: "auto"
        }}>
        <Title order={3} mb="md" style={{ color: "#6b5ca5" }}>
          Editing Document: {selectedDocument.id}
        </Title>

        {/* Render All Fields */}
        {Object.entries(selectedDocument.fields).map(([field, value]) => (
          <div key={field} style={{ marginBottom: "10px" }}>
            {Array.isArray(value) ? (
              <>
                <Title order={4} style={{ fontSize: "16px", color: "#6b5ca5" }}>
                  {field}
                </Title>
                <List spacing="xs">
                  {value.map((item, index) => (
                    <List.Item key={index}>
                      {typeof item === "object" ? (
                        <>
                          {Object.entries(item).map(([key, val]) => (
                            <Flex
                              key={key}
                              align="center"
                              gap="sm"
                              style={{ marginBottom: "5px" }}>
                              <span
                                style={{
                                  width: "120px",
                                  fontWeight: "bold",
                                  color: "#6b5ca5"
                                }}>
                                {key}:
                              </span>
                              <TextInput
                                value={val}
                                onChange={(e) =>
                                  handleUpdateObjectField(
                                    field,
                                    index,
                                    key,
                                    e.target.value
                                  )
                                }
                                style={{ flexGrow: 1 }}
                              />
                            </Flex>
                          ))}
                        </>
                      ) : (
                        <Flex
                          align="center"
                          gap="sm"
                          style={{ marginBottom: "5px" }}>
                          <span
                            style={{
                              width: "120px",
                              fontWeight: "bold",
                              color: "#6b5ca5"
                            }}>
                            {field}:
                          </span>
                          <TextInput
                            value={item}
                            onChange={(e) =>
                              handleUpdateField(field, e.target.value)
                            }
                            style={{ flexGrow: 1 }}
                          />
                        </Flex>
                      )}
                      <Button
                        variant="outline"
                        color="red"
                        size="xs"
                        onClick={() => handleRemoveArrayItem(field, index)}
                        mt="xs">
                        Remove
                      </Button>
                    </List.Item>
                  ))}
                </List>
                <Button
                  onClick={() => handleAddArrayItem(field)}
                  size="xs"
                  mt="sm">
                  + Add Item
                </Button>
              </>
            ) : (
              <Flex align="center" gap="sm">
                <span
                  style={{
                    width: "120px",
                    fontWeight: "bold",
                    color: "#6b5ca5"
                  }}>
                  {field}:
                </span>
                <TextInput
                  value={value}
                  onChange={(e) => handleUpdateField(field, e.target.value)}
                  style={{ flexGrow: 1 }}
                />
              </Flex>
            )}
          </div>
        ))}

        {/* Save Button */}
        <Group position="right" mt="md">
          <Button
            onClick={handleSave}
            loading={saving}
            disabled={saving}
            color="indigo">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Group>
      </Paper>
    </Container>
  );
};

export default Edit;
